import { useState, useRef, useEffect, lazy, Suspense } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowRight, ArrowLeft, Lock,
  Search, Sparkles, Grid3x3, Layers, MessagesSquare,
  Camera, Map, AlertTriangle, GitBranch, Users,
  GraduationCap, Gamepad2, BookOpen, Radar,
  LayoutDashboard, ClipboardList, ClipboardCheck, FileText, ListChecks, CalendarRange, UserCircle, Settings,
} from "lucide-react";
import { T } from "./i18n";
import { useConfigStore } from "./stores/configStore";
import { useNavigationStore } from "./stores/navigationStore";
import { useAIEngineStore, DEFAULT_AI_ENGINE_SETTINGS, normalizeAIEngineSettings } from "./stores/aiEngineStore";
import { ClauseModal } from "./components/ClauseModal";
import { NRModal } from "./components/NRModal";
import { AIEngineConfigPanel } from "./components/AIEngineConfigPanel";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./components/ui/dialog";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "./components/ui/tabs";
import { Badge } from "./components/ui/badge";
import { StandardLoaderButton, useStandardsToggle, StandardLoader } from "./modules/standards/StandardLoader";
import MATRIX from "./data/matrix.json";
import NR_DETAILS from "./data/nrDetails.json";
import { PrecisionDebugPanel } from "./components/PrecisionDebugPanel";
import { registerPrecisionDebug } from "./utils/precisionDebug";
import { useAuditLogStore } from "./stores/auditLogStore";

// Lazy-loaded modules
const lazyNamed = (loader, name) => lazy(() => loader().then(m => ({ default: m[name] })));
const MetodoPortoesTab = lazyNamed(() => import("./modules/tools/MetodoPortoesTab"), "MetodoPortoesTab");
const PhotoTab         = lazyNamed(() => import("./modules/tools/PhotoTab"),        "PhotoTab");
const TrailsTab        = lazyNamed(() => import("./modules/tools/TrailsTab"),       "TrailsTab");
const NCsTab           = lazyNamed(() => import("./modules/tools/NCsTab"),          "NCsTab");
const ChangeManagement = lazyNamed(() => import("./modules/tools/ChangeManagement"),"ChangeManagement");
const MeetingTool      = lazyNamed(() => import("./modules/tools/MeetingTool"),     "MeetingTool");
const AuditProgram     = lazyNamed(() => import("./modules/audit/AuditProgram"),    "AuditProgram");
const QuizMode         = lazyNamed(() => import("./modules/concepts/QuizMode"),     "QuizMode");
const JourneyView      = lazyNamed(() => import("./modules/academia/JourneyView"),  "JourneyView");
const ReferenceView    = lazyNamed(() => import("./modules/academia/ReferenceView"),"ReferenceView");
const RadarView        = lazyNamed(() => import("./modules/academia/RadarView"),    "RadarView");
const ExplorerShell    = lazyNamed(() => import("./modules/explorer/ExplorerShell"),"ExplorerShell");
const DATA_FILE_LOADERS = import.meta.glob("./data/**/*.json");

function ModeFallback() {
  return <div className="text-center py-16 text-muted-foreground text-xs font-mono tracking-widest">carregando…</div>;
}

const AUDIT_PROGRAM_TABS = [
  { key: "dashboard" }, { key: "preparation" }, { key: "checklist" }, { key: "report" }, { key: "followup" }, { key: "program" }, { key: "profile" },
];
const TOOLS_TABS = [
  { key: "cenario" }, { key: "photo" }, { key: "trails" }, { key: "ncs" }, { key: "changes" }, { key: "meetings" },
];
const ACADEMIA_TABS = [
  { key: "journey" }, { key: "simulator" }, { key: "reference" }, { key: "radar" },
];
const EXPLORER_TABS = [
  { key: "search" }, { key: "consulta" }, { key: "semantic" }, { key: "matrix" }, { key: "detail" },
];

const URL_TO_TAB = {
  setup:"setup", program:"program", dashboard:"dashboard", preparation:"preparation", checklist:"checklist", report:"report", followup:"followup", profile:"profile",
  photo:"photo", trails:"trails", ncs:"ncs", changes:"changes", meetings:"meetings",
  analise:"cenario", ai:"consulta", consulta:"consulta", cenario:"cenario", assistente:"cenario",
  journey:"journey", simulator:"simulator", reference:"reference", radar:"radar",
  search:"search", matrix:"matrix", detail:"detail", semantic:"semantic",
};
const TAB_TO_URL = {
  journey:"journey", simulator:"simulator", reference:"reference", radar:"radar",
  search:"search", matrix:"matrix", detail:"detail", semantic:"semantic",
  consulta:"consulta", cenario:"cenario",
};
const tabToUrl = (tab) => TAB_TO_URL[tab] || tab;
const VALID_MODES = new Set(["audit", "tools", "academia", "explorer"]);

// Icon map for global TabsTrigger — keyed by tab key (mode-agnostic)
const TAB_ICONS = {
  // Explorer
  search: Search, semantic: Sparkles, matrix: Grid3x3, detail: Layers,
  // Tools
  consulta: MessagesSquare, cenario: Sparkles, photo: Camera, trails: Map, ncs: AlertTriangle, changes: GitBranch, meetings: Users,
  // Academia
  journey: GraduationCap, simulator: Gamepad2, reference: BookOpen, radar: Radar,
  // Audit
  dashboard: LayoutDashboard, preparation: ClipboardList, checklist: ClipboardCheck, report: FileText, followup: ListChecks, program: CalendarRange, profile: UserCircle, setup: Settings,
};

// MODE_LABEL is now derived from T[lang] inside render

export default function App() {
  const navigate = useNavigate();
  const params = useParams();

  const lang = useConfigStore(s => s.lang);
  const setLang = useConfigStore(s => s.setLang);
  const mode = useNavigationStore(s => s.mode);
  const tabs = useNavigationStore(s => s.tabs);
  const setMode = useNavigationStore(s => s.setMode);
  const setTabFn = useNavigationStore(s => s.setTab);
  const goModeFn = useNavigationStore(s => s.goMode);
  const engineSettings = useAIEngineStore(s => s.settings);
  const setEngineSettings = useAIEngineStore(s => s.setSettings);

  useEffect(() => {
    const urlMode = params.mode && VALID_MODES.has(params.mode) ? params.mode : null;
    const urlTab = params.tab ? URL_TO_TAB[params.tab] : null;
    if (urlMode !== mode) setMode(urlMode);
    if (urlMode && urlTab && tabs[urlMode] !== urlTab) setTabFn(urlMode, urlTab);
  }, [params.mode, params.tab]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    registerPrecisionDebug({ getLog: () => useAuditLogStore.getState().log });
  }, []);

  const debugView = typeof window !== "undefined" && new URLSearchParams(window.location.search).get("debug") === "precision";

  const tab = tabs[mode] || null;
  const setTab = (t) => { setTabFn(mode, t); if (mode) navigate(`/${mode}/${tabToUrl(t)}`); };
  const goMode = (m, t) => {
    goModeFn(m, t);
    if (m && t) navigate(`/${m}/${tabToUrl(t)}`);
    else if (m) navigate(`/${m}`);
    else navigate("/");
  };

  const t = T[lang];
  const [betaPassword, setBetaPassword] = useState("");
  const [betaError, setBetaError] = useState("");
  const [betaContact, setBetaContact] = useState(null);
  const [betaAuthorized, setBetaAuthorized] = useState(false);
  const [betaRole, setBetaRole] = useState(null);
  const [betaChecking, setBetaChecking] = useState(true);
  const [betaSessionEnded, setBetaSessionEnded] = useState(false);

  const buildEngineSettingsForRole = (role, enginePolicy = null) => {
    const base = normalizeAIEngineSettings(DEFAULT_AI_ENGINE_SETTINGS);
    if (role !== "tester") return base;

    const forcedProfile = enginePolicy?.profile === "balanced" || enginePolicy?.profile === "strict"
      ? enginePolicy.profile
      : "economy";

    if (forcedProfile === "balanced") return base;
    if (forcedProfile === "strict") {
      return normalizeAIEngineSettings({
        ...base,
        profile: "strict",
        deepthink: "high",
        contextMode: "broad",
        qualityMode: "strict",
        breadthMode: "wide",
        maxTokens: { ...base.maxTokens, precision: 16384, trails: 16384, fast: 8192 },
      });
    }

    return normalizeAIEngineSettings({
      ...base,
      profile: "economy",
      deepthink: "low",
      contextMode: "focused",
      qualityMode: "fast",
      breadthMode: "narrow",
      maxTokens: { ...base.maxTokens, precision: 6144, trails: 6144, fast: 2048 },
    });
  };

  const applyBetaSession = (authorized, role, enginePolicy = null) => {
    if (!authorized) {
      setBetaAuthorized(false);
      setBetaRole(null);
      return;
    }

    const normalizedRole = role === "tester" ? "tester" : "admin";
    setBetaAuthorized(true);
    setBetaRole(normalizedRole);
    setEngineSettings(buildEngineSettingsForRole(normalizedRole, enginePolicy));
  };

  useEffect(() => {
    if (import.meta.env.DEV) {
      applyBetaSession(true, "admin");
      setBetaChecking(false);
      return;
    }
    let cancelled = false;
    const check = async () => {
      try {
        const hadSession = sessionStorage.getItem("norm_beta_had_session") === "1";
        const resp = await fetch("/api/beta-access", { credentials: "include" });
        const data = await resp.json();
        if (!cancelled) {
          const authorized = Boolean(data?.authorized);
          applyBetaSession(authorized, data?.role, data?.engine_policy || null);
          if (authorized) {
            sessionStorage.setItem("norm_beta_had_session", "1");
            setBetaSessionEnded(false);
          } else if (hadSession) {
            setBetaSessionEnded(true);
          }
        }
      } catch (e) {
        if (!cancelled) applyBetaSession(false, null);
      } finally {
        if (!cancelled) setBetaChecking(false);
      }
    };
    check();
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const unlockBeta = async () => {
    try {
      const resp = await fetch("/api/beta-access", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ password: betaPassword }),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        if (resp.status === 403 && err?.code === "contact_required") {
          setBetaContact({ email: err.contact_email || "", whatsapp: err.contact_whatsapp || "" });
          setBetaError(t.authContactRequired || "Access temporarily blocked. Contact the administrator.");
          return;
        }
        setBetaError(t.authInvalidPassword || "Invalid access password.");
        return;
      }
      const data = await resp.json().catch(() => ({ authorized: true, role: "admin" }));
      applyBetaSession(Boolean(data?.authorized), data?.role, data?.engine_policy || null);
      sessionStorage.setItem("norm_beta_had_session", "1");
      setBetaSessionEnded(false);
      setBetaError("");
      setBetaContact(null);
      setBetaPassword("");
    } catch (e) {
      setBetaError(t.authServerUnavailable || "Unable to validate beta access.");
    }
  };

  const logoutBeta = async () => {
    try {
      await fetch("/api/beta-access", { method: "DELETE", credentials: "include" });
    } catch (e) {}
    setBetaAuthorized(false);
    setBetaRole(null);
    setBetaPassword("");
    setBetaError("");
    setBetaSessionEnded(false);
    sessionStorage.removeItem("norm_beta_had_session");
    setEngineConfigOpen(false);
    goMode(null);
  };

  // Onboarding state
  const [onboarded, setOnboarded] = useState(() => localStorage.getItem("norm_onboarded") === "true");
  const [onbStep, setOnbStep] = useState(1);
  const [onbDialogOpen, setOnbDialogOpen] = useState(false);
  const { activeStandards, activeCount } = useStandardsToggle();

  // Dynamic clause count: MATRIX + extra active standards
  const SGI_IDS = new Set(["iso9001", "iso14001", "iso45001", "iso19011"]);
  const [extraClauseCount, setExtraClauseCount] = useState(0);
  useEffect(() => {
    const extras = activeStandards.filter(s => !SGI_IDS.has(s.id) && s.dataFile);
    if (!extras.length) { setExtraClauseCount(0); return; }
    let cancelled = false;
    Promise.all(extras.map(s => {
      const key = `./data/${s.dataFile}`;
      const load = DATA_FILE_LOADERS[key];
      if (!load) return Promise.resolve(0);
      return load().then(mod => {
        const d = mod.default || mod;
        return Array.isArray(d) ? d.length : Object.keys(d).length;
      }).catch(() => 0);
    })).then(counts => { if (!cancelled) setExtraClauseCount(counts.reduce((a, b) => a + b, 0)); });
    return () => { cancelled = true; };
  }, [activeStandards.map(s => s.id).join(",")]); // eslint-disable-line react-hooks/exhaustive-deps
  const totalClauses = MATRIX.length + extraClauseCount;
  const finishOnboarding = () => { localStorage.setItem("norm_onboarded", "true"); setOnboarded(true); };
  const resetOnboarding = () => { localStorage.removeItem("norm_onboarded"); setOnboarded(false); setOnbStep(1); };

  const [modalClause, setModalClause] = useState(null);
  const [nrModal, setNrModal] = useState(null);
  const [engineConfigOpen, setEngineConfigOpen] = useState(false);
  const engineProfileLabel = !engineSettings?.enabled
    ? "Engine Off"
    : engineSettings?.profile === "economy"
      ? "Economia"
      : engineSettings?.profile === "strict"
        ? "Máx. Precisão"
        : "Balanceado";
  const engineProfileTone = !engineSettings?.enabled
    ? {
        badgeClass: "border-zinc-300 bg-zinc-100 text-zinc-800",
        buttonClass: "border-zinc-300 text-zinc-800 hover:bg-zinc-100",
      }
    : engineSettings?.profile === "economy"
      ? {
          badgeClass: "border-emerald-300 bg-emerald-100 text-emerald-900",
          buttonClass: "border-emerald-300 text-emerald-900 hover:bg-emerald-100",
        }
      : engineSettings?.profile === "strict"
        ? {
            badgeClass: "border-rose-300 bg-rose-100 text-rose-900",
            buttonClass: "border-rose-300 text-rose-900 hover:bg-rose-100",
          }
        : {
            badgeClass: "border-amber-300 bg-amber-100 text-amber-900",
            buttonClass: "border-amber-300 text-amber-900 hover:bg-amber-100",
          };
  const canConfigureEngine = betaRole === "admin";

  useEffect(() => {
    if (!canConfigureEngine && engineConfigOpen) setEngineConfigOpen(false);
  }, [canConfigureEngine, engineConfigOpen]);
  const navigateToClause = (clauseNum) => { goMode("academia", "reference"); };

  const tabLabels = {
    analise: t.tabAnalise, photo: t.tabPhoto, trails: t.tabTrails, ncs: t.tabNCs, changes: t.tabChanges, meetings: t.tabMeetings,
    journey: t.tabJourney, simulator: t.tabSimulator, reference: t.tabReference, radar: t.tabRadar,
    setup: t.tabSetup, program: t.tabProgram, dashboard: t.tabDashboard, preparation: t.tabPreparation, checklist: t.tabChecklist, report: t.tabReport, followup: t.tabFollowup, profile: t.tabProfile,
    search: t.tabSearch || "Busca", matrix: t.tabMatrix || "Matriz HLS", detail: t.tabDetail || "Detalhe", semantic: t.tabSemantic || "Por tema",
    consulta: t.tabConsulta || "Consulta", cenario: t.tabCenario || "Cenário",
  };

  const currentTabs = mode === "audit" ? AUDIT_PROGRAM_TABS : mode === "tools" ? TOOLS_TABS : mode === "explorer" ? EXPLORER_TABS : ACADEMIA_TABS;

  const HOME_CARDS = [
    { key: "audit",    title: t.homeAudit,    badge: t.homeBadgeAudit,    desc: t.homeDescAudit,    onClick: () => goMode("audit", "dashboard") },
    { key: "tools",    title: t.homeTools,    badge: t.homeBadgeTools,    desc: t.homeDescTools,    onClick: () => goMode("tools", "cenario") },
    { key: "academia", title: t.homeConcepts, badge: t.homeBadgeConcepts, desc: t.homeDescConcepts, onClick: () => goMode("academia", "journey") },
    { key: "explorer", title: t.homeExplorer || "Norm Explorer", badge: t.homeBadgeExplorer || "EXPLORADOR", desc: t.homeDescExplorer || "Busca cross-norma, matriz HLS e cruzamentos entre normas.", onClick: () => goMode("explorer", "search") },
  ];

  if (debugView) return <PrecisionDebugPanel />;

  return (
    <div className="font-sans bg-background min-h-screen text-foreground">
      {betaChecking && (
        <div className="min-h-screen flex items-center justify-center px-6">
          <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-muted-foreground">checking beta access…</div>
        </div>
      )}
      {!betaChecking && (
        <>
      {!betaAuthorized && (
        <div className="min-h-screen flex items-center justify-center px-6">
          <div className="w-full max-w-2xl bg-card border border-border rounded-xl p-6 sm:p-8">
            <div className="text-center">
              <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-primary mb-2">{t.authBetaTag || "beta access"}</div>
              <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-foreground mb-2">NormAssurance</h1>
            </div>
            <p className="text-[13px] text-muted-foreground mb-4 leading-relaxed text-center max-w-xl mx-auto">
              {t.authBetaDesc || "This beta is protected. Enter the access password to continue."}
            </p>
            <div className="bg-secondary border border-border rounded-lg p-4 mb-4">
              <div className="font-mono text-[9px] tracking-[0.18em] uppercase text-primary mb-2 text-center">
                {t.authBriefTitle || "Built for serious auditors"}
              </div>
              <div className="space-y-2 text-[12px] text-muted-foreground leading-relaxed text-justify">
                <div>{t.authBriefWhat}</div>
                <div>{t.authBriefWhy}</div>
                <div>{t.authBriefHow}</div>
                {t.authBetaScope && (
                  <div className="text-[11px] text-muted-foreground/80 italic mt-1">{t.authBetaScope}</div>
                )}
                <div className="pt-1 border-t border-border/70">
                  <div><strong className="text-foreground">{t.authModuleProgramTitle || "Audit Program"}:</strong> {t.authModuleProgramDesc}</div>
                  <div><strong className="text-foreground">{t.authModuleToolsTitle || "AI Field Tools"}:</strong> {t.authModuleToolsDesc}</div>
                  <div><strong className="text-foreground">{t.authModuleAcademyTitle || "Academy"}:</strong> {t.authModuleAcademyDesc}</div>
                </div>
              </div>
            </div>
            {betaSessionEnded && !betaError && (
              <div className="text-[11px] text-muted-foreground leading-relaxed bg-secondary border border-border rounded p-2 mb-3 max-w-md mx-auto">
                {t.authSessionExpired}
              </div>
            )}
            <div className="space-y-3 max-w-md mx-auto">
              <Input
                type="password"
                value={betaPassword}
                onChange={(e) => { setBetaPassword(e.target.value); if (betaError) setBetaError(""); }}
                placeholder={t.authPasswordPlaceholder || "Access password"}
                onKeyDown={(e) => { if (e.key === "Enter") unlockBeta(); }}
                className="h-12 text-[15px] rounded-lg"
              />
              {betaError && <div className="text-[11px] text-destructive">{betaError}</div>}
              {betaContact && (
                <div className="text-[11px] text-muted-foreground leading-relaxed bg-secondary border border-border rounded p-2">
                  {betaContact.email && <div>{t.authContactEmail || "Email"}: {betaContact.email}</div>}
                  {betaContact.whatsapp && <div>{t.authContactWhatsapp || "WhatsApp"}: {betaContact.whatsapp}</div>}
                </div>
              )}
              <Button onClick={unlockBeta} className="w-full h-12 text-[15px] rounded-lg">
                {t.authUnlock || "Unlock beta"}
              </Button>
            </div>
          </div>
        </div>
      )}
      {betaAuthorized && (
        <>

      {/* HOME */}
      <div className={mode ? "hidden" : "block"}>
        <div className="max-w-[1100px] mx-auto px-6 py-12 sm:py-14 border-b border-border">
          <div className="relative flex items-end justify-between gap-6 flex-wrap">
            <div>
              <div className="font-mono text-[10px] font-medium tracking-[0.22em] text-primary uppercase mb-3.5">{t.homeTagline}</div>
              <h1 className="font-display text-7xl sm:text-9xl font-bold tracking-tight text-foreground leading-[0.85] m-0">Norm.</h1>
              <p className="text-sm text-muted-foreground mt-4 max-w-md leading-relaxed">{t.homeDesc}</p>
            </div>

            <div className="flex flex-col items-end gap-4">
              <div className="flex gap-1 items-center">
                {[["en", "EN"], ["es", "ES"], ["pt", "PT"], ["de", "DE"]].map(([code, label]) => (
                  <button
                    key={code}
                    onClick={() => setLang(code)}
                    className={`font-mono px-2.5 py-1 text-[9px] font-semibold rounded border tracking-wider cursor-pointer transition-colors ${
                      lang === code
                        ? "bg-primary/15 border-primary/55 text-primary"
                        : "bg-transparent border-border text-muted-foreground hover:bg-secondary"
                    }`}
                  >
                    {label}
                  </button>
                ))}
                <Button variant="outline" size="sm" onClick={logoutBeta} className="font-mono text-[9px] h-7 px-2.5">
                  {t.authLogout || "logout"}
                </Button>
              </div>
              <div className="flex flex-wrap items-center gap-2 justify-end">
                <StandardLoaderButton />
                {canConfigureEngine && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setEngineConfigOpen(true)}
                    className={`font-mono text-[9px] h-8 px-2.5 ${engineProfileTone.buttonClass}`}
                  >
                    <Settings className="h-3.5 w-3.5 mr-1" />
                    engine ia · {engineProfileLabel}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-[1100px] mx-auto px-6 py-6">
          {!onboarded ? (
            /* ── ONBOARDING FLOW ── */
            <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
              {onbStep === 1 && (
                <div className="text-center py-8">
                  <div className="font-display text-5xl sm:text-7xl font-bold text-foreground tracking-tight mb-4">Norm.</div>
                  <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-primary mb-6">{t.onbSubtitle}</div>
                  <div className="text-[14px] text-muted-foreground leading-relaxed max-w-lg mx-auto mb-8">{t.onbBullets}</div>
                  <div className="flex gap-3 justify-center">
                    <Button onClick={() => setOnbDialogOpen(true)}>{t.onbConfigNorms}</Button>
                    <Button variant="outline" onClick={() => setOnbStep(2)}>{t.onbSkip}</Button>
                  </div>
                  <StandardLoader open={onbDialogOpen} onOpenChange={(open) => { setOnbDialogOpen(open); if (!open) setOnbStep(2); }} />
                </div>
              )}
              {onbStep === 2 && (
                <div className="text-center py-8 animate-in fade-in">
                  <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-success mb-3">{t.onbLoaded}</div>
                  <div className="font-display text-3xl font-semibold text-foreground tracking-tight mb-2">
                    {activeCount} {t.onbActive} · {MATRIX.length} {t.onbClauses}
                  </div>
                  {t.dataPrivacyNotice && (
                    <p className="text-[9px] text-muted-foreground/60 font-mono leading-relaxed max-w-sm mx-auto mt-4 text-center">
                      <Lock className="w-3 h-3 inline mr-1" /> {t.dataPrivacyNotice}
                    </p>
                  )}
                  <Button className="mt-6" onClick={finishOnboarding}>{t.onbStart}</Button>
                </div>
              )}
            </div>
          ) : (
            /* ── NORMAL HOME CARDS ── */
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 animate-in fade-in slide-in-from-bottom-4 duration-500">
                {HOME_CARDS.map(card => (
                  <div
                    key={card.key}
                    onClick={card.onClick}
                    className="bg-card border border-border rounded-xl cursor-pointer transition-all hover:shadow-lg hover:-translate-y-1 group relative overflow-hidden min-h-[200px] flex flex-col"
                  >
                    <div className="absolute top-0 left-0 w-full h-[3px] bg-primary rounded-t-xl" />
                    <div className="p-6 pt-8 flex-1 flex flex-col items-center text-center justify-center">
                      <div className="font-display text-3xl sm:text-4xl font-bold text-foreground tracking-tight leading-none mb-2">{card.title}</div>
                      <div className="font-mono text-[8px] tracking-[0.2em] uppercase text-primary mb-4">{card.badge}</div>
                      <div className="text-[13px] text-muted-foreground leading-relaxed max-w-[260px]">{card.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        <div className="max-w-[1100px] mx-auto px-6 mt-10 pt-8 pb-12 font-mono text-center text-[9px] text-muted-foreground tracking-[0.18em] uppercase border-t border-border">
          {t.homeFooter}
          {onboarded && (
            <button onClick={resetOnboarding} className="block mx-auto mt-3 font-mono text-[9px] text-muted-foreground/50 hover:text-muted-foreground cursor-pointer tracking-wider uppercase transition-colors">
              {t.onbReconfigure}
            </button>
          )}
          <div className="mt-3 font-mono text-[8px] text-muted-foreground/40 tracking-wider normal-case">
            build {__BUILD_COMMIT__} · {__BUILD_DATE__}
          </div>
          <div className="flex gap-6 justify-center mt-4">
            <a href="/NORM_core_diagram.html" target="_blank" rel="noopener noreferrer" className="font-mono text-[9px] text-muted-foreground/50 hover:text-muted-foreground tracking-wider uppercase transition-colors">
              Core Diagram ↗
            </a>
            <a href="/NORM_organogram.html" target="_blank" rel="noopener noreferrer" className="font-mono text-[9px] text-muted-foreground/50 hover:text-muted-foreground tracking-wider uppercase transition-colors">
              Organograma ↗
            </a>
          </div>
        </div>
      </div>

      {canConfigureEngine && (
        <Dialog open={engineConfigOpen} onOpenChange={setEngineConfigOpen}>
          <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Engine IA · Configuração Global e por Ferramenta</DialogTitle>
              <DialogDescription>
                Ajuste em popup e continue o trabalho no módulo atual sem perder contexto.
              </DialogDescription>
            </DialogHeader>
            <AIEngineConfigPanel />
          </DialogContent>
        </Dialog>
      )}

      {/* INNER MODES */}
      {mode && (
        <div>
          <div className="bg-secondary border-b border-border sticky top-0 z-50 shadow-lg">
            <div className="max-w-[1100px] mx-auto px-6 pt-3 pb-2">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-3">
                  <Button variant="outline" size="sm" onClick={() => goMode(null)} className="font-mono text-[10px]">
                    <ArrowLeft className="h-4 w-4 mr-1" /> home
                  </Button>
                  <Button variant="outline" size="sm" onClick={logoutBeta} className="font-mono text-[10px]">
                    {t.authLogout || "logout"}
                  </Button>
                  {canConfigureEngine && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setEngineConfigOpen(true)}
                      className={`font-mono text-[10px] ${engineProfileTone.buttonClass}`}
                    >
                      <Settings className="h-4 w-4 mr-1" /> engine ia · {engineProfileLabel}
                    </Button>
                  )}
                  <div className="flex items-baseline gap-2.5">
                    <span className="font-display text-2xl font-bold text-foreground tracking-tight">Norm.</span>
                    <Badge variant="default">{{ audit:t.modeAudit, tools:t.modeTools, academia:t.modeConcepts }[mode] || mode}</Badge>
                    <Badge variant="outline" className={engineProfileTone.badgeClass}>{engineProfileLabel}</Badge>
                  </div>
                </div>
                <div className="flex gap-0.5">
                  {[["en", "EN"], ["es", "ES"], ["pt", "PT"], ["de", "DE"]].map(([code, label]) => (
                    <button
                      key={code}
                      onClick={() => setLang(code)}
                      className={`font-mono px-2 py-0.5 text-[8px] font-semibold rounded border tracking-wider cursor-pointer transition-colors ${
                        lang === code
                          ? "bg-primary/15 border-primary/55 text-primary"
                          : "bg-transparent border-transparent text-muted-foreground hover:bg-secondary"
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              <Tabs value={tab || ""} onValueChange={setTab}>
                <TabsList>
                  {currentTabs.map(tb => {
                    const Icon = TAB_ICONS[tb.key];
                    return (
                      <TabsTrigger key={tb.key} value={tb.key}>
                        {Icon && <Icon />}
                        {tabLabels[tb.key] || tb.key}
                      </TabsTrigger>
                    );
                  })}
                </TabsList>
              </Tabs>
            </div>
          </div>

          <div className="max-w-[1100px] mx-auto px-6 pt-8 pb-6 animate-in fade-in slide-in-from-bottom-4 duration-300">
            <Suspense fallback={<ModeFallback />}>
              {mode === "audit" && <AuditProgram lang={lang} tab={tab} setTab={setTab} goMode={goMode} />}
              {tab === "cenario" && <MetodoPortoesTab lang={lang} />}
              {tab === "photo" && <PhotoTab lang={lang} />}
              {tab === "trails" && <TrailsTab lang={lang} />}
              {tab === "ncs" && <NCsTab lang={lang} />}
              {tab === "changes" && <ChangeManagement lang={lang} />}
              {tab === "meetings" && <MeetingTool lang={lang} />}
              {tab === "journey" && <JourneyView lang={lang} goMode={goMode} />}
              {tab === "simulator" && <QuizMode lang={lang} />}
              {tab === "reference" && <ReferenceView lang={lang} onModal={setModalClause} />}
              {tab === "radar" && <RadarView lang={lang} />}
              {mode === "explorer" && <ExplorerShell lang={lang} tab={tab || "search"} setTab={setTab} goMode={goMode} />}
            </Suspense>

          </div>

          {modalClause && (
            typeof modalClause === "object"
              ? <ClauseModal clauseId={modalClause.clauseId} normaId={modalClause.normaId} onClose={() => setModalClause(null)} />
              : <ClauseModal clauseId={modalClause} onClose={() => setModalClause(null)} />
          )}
          {nrModal && <NRModal nrId={nrModal} onClose={() => setNrModal(null)} />}

          <div className="max-w-[1100px] mx-auto px-6 mt-8 pt-6 pb-10 font-mono text-center text-[9px] text-muted-foreground tracking-[0.18em] uppercase border-t border-border">
            {t.homeFooter} · {totalClauses} {t.records} · {Object.keys(NR_DETAILS).length} NRs
          </div>
        </div>
      )}
        </>
      )}
        </>
      )}
    </div>
  );
}

function Stat({ label, value, suffix, color = "text-foreground" }) {
  return (
    <div>
      <div className="font-mono text-[9px] tracking-wider uppercase text-muted-foreground mb-1">{label}</div>
      <div className={`font-display text-3xl font-semibold tracking-tight leading-none ${color}`}>
        {value}
        {suffix && <span className="text-sm text-muted-foreground ml-0.5">{suffix}</span>}
      </div>
    </div>
  );
}
